import { useCallback, useMemo, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { NavLink } from "react-router";
import {
  Plus,
  Briefcase,
  AlertTriangle,
  GripVertical,
} from "lucide-react";
import { useToast } from "../../../components/ui/Toast";
import {
  ALL_BOOKING_TYPES,
  ALL_PRICING_PLANS,
  UPDATE_BOOKING_TYPE,
} from "./queries";
import type { BookingTypeRecord, PricingPlanRecord } from "./types";

interface ServiceItemProps {
  svc: BookingTypeRecord;
  plansById: Map<string, PricingPlanRecord>;
  dragging: boolean;
  dropAbove: boolean;
  dropBelow: boolean;
  onDragStart: () => void;
  onDragOver: (e: React.DragEvent) => void;
  onDragEnd: () => void;
  onDrop: (e: React.DragEvent) => void;
}

function ServiceItem({
  svc,
  plansById,
  dragging,
  dropAbove,
  dropBelow,
  onDragStart,
  onDragOver,
  onDragEnd,
  onDrop,
}: ServiceItemProps) {
  let priceLine: string;
  if (svc.pricingPlans.length === 0) {
    priceLine = "No plan";
  } else if (svc.pricingPlans.length === 1) {
    const plan = plansById.get(svc.pricingPlans[0].pricingPlanId);
    priceLine = plan?.name ?? "Missing plan";
  } else {
    priceLine = `${svc.pricingPlans.length} tiers`;
  }

  const stateLine = svc.state ?? "All states";

  return (
    <div
      draggable
      onDragStart={(e) => {
        e.dataTransfer.effectAllowed = "move";
        // Firefox needs some data set on the transfer or it won't fire dragover.
        e.dataTransfer.setData("text/plain", svc.id);
        onDragStart();
      }}
      onDragOver={onDragOver}
      onDragEnd={onDragEnd}
      onDrop={onDrop}
      className={`relative ${dragging ? "opacity-40" : ""}`}
    >
      {/* Drop indicators above/below the row */}
      {dropAbove && (
        <div className="absolute left-2 right-2 -top-px h-0.5 bg-tide rounded pointer-events-none z-10" />
      )}
      {dropBelow && (
        <div className="absolute left-2 right-2 -bottom-px h-0.5 bg-tide rounded pointer-events-none z-10" />
      )}
      <NavLink
        to={`services/${svc.id}`}
        draggable={false}
        className={({ isActive }) =>
          `block rounded-md pl-1 pr-2 py-2 transition ${
            isActive
              ? "bg-tide-ss text-ink"
              : "hover:bg-paper-2 text-ink-2"
          } ${!svc.isActive ? "opacity-60" : ""}`
        }
      >
        <div className="flex items-center gap-1.5">
          <span
            className="text-ink-6 cursor-grab active:cursor-grabbing flex-shrink-0"
            // Don't navigate when the user mouses down on the grip — they're
            // about to drag, not click.
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            aria-hidden="true"
          >
            <GripVertical size={12} strokeWidth={1.5} />
          </span>
          <div className="w-7 h-7 rounded-md bg-canvas-2 flex items-center justify-center flex-shrink-0">
            <Briefcase size={12} strokeWidth={2} className="stroke-ink-4" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5">
              <span className="text-[12.5px] font-medium truncate">
                {svc.name}
              </span>
              {!svc.isActive && (
                <span className="text-[9.5px] uppercase tracking-wider text-ink-5 font-semibold flex-shrink-0">
                  paused
                </span>
              )}
            </div>
            <div className="text-[10.5px] text-ink-4 truncate">
              {stateLine} · {priceLine}
            </div>
          </div>
        </div>
      </NavLink>
    </div>
  );
}

function PlanItem({
  plan,
  usageCount,
}: {
  plan: PricingPlanRecord;
  usageCount: number;
}) {
  return (
    <NavLink
      to={`plans/${plan.id}`}
      className={({ isActive }) =>
        `block rounded-md px-2 py-2 transition ${
          isActive
            ? "bg-tide-ss text-ink"
            : "hover:bg-paper-2 text-ink-2"
        }`
      }
    >
      <div className="flex items-center gap-2">
        <div className="flex-1 min-w-0">
          <div className="text-[12.5px] font-medium truncate">{plan.name}</div>
          <div className="text-[10.5px] text-ink-4 num truncate">
            ${plan.authAmount.toLocaleString()} + $
            {plan.hourlyRate.toLocaleString()}/hr
          </div>
        </div>
        <span
          className={`text-[10px] num flex-shrink-0 ${
            usageCount === 0 ? "text-ink-5" : "text-ink-4"
          }`}
        >
          {usageCount === 0 ? "unused" : `${usageCount}×`}
        </span>
      </div>
    </NavLink>
  );
}

interface DragState {
  fromIdx: number;
  // Insert position: drop will place the moved row at this index in the new
  // array. Equals services.length when dropping past the last item.
  toIdx: number;
}

export function CatalogRail() {
  const toast = useToast();

  const btQuery = useQuery<{ allBookingTypes: BookingTypeRecord[] }>(
    ALL_BOOKING_TYPES,
    { fetchPolicy: "cache-and-network" },
  );
  const ppQuery = useQuery<{ allPricingPlans: PricingPlanRecord[] }>(
    ALL_PRICING_PLANS,
    { fetchPolicy: "cache-and-network" },
  );

  const [updateBookingType] = useMutation(UPDATE_BOOKING_TYPE);
  const [drag, setDrag] = useState<DragState | null>(null);

  const services = btQuery.data?.allBookingTypes ?? [];
  const plans = ppQuery.data?.allPricingPlans ?? [];

  const plansById = useMemo(() => {
    const map = new Map<string, PricingPlanRecord>();
    for (const p of plans) map.set(p.id, p);
    return map;
  }, [plans]);

  const planUsage = useMemo(() => {
    const counts = new Map<string, number>();
    for (const svc of services) {
      for (const t of svc.pricingPlans) {
        counts.set(t.pricingPlanId, (counts.get(t.pricingPlanId) ?? 0) + 1);
      }
    }
    return counts;
  }, [services]);

  const handleReorder = useCallback(
    async (fromIdx: number, toIdx: number) => {
      // toIdx is an insertion point. After splice-out from fromIdx, an
      // insertion at toIdx > fromIdx is one less than expected. Normalise so
      // that "drop on the same slot" is a no-op.
      const adjustedTo = toIdx > fromIdx ? toIdx - 1 : toIdx;
      if (adjustedTo === fromIdx) return;

      const reordered = [...services];
      const [moved] = reordered.splice(fromIdx, 1);
      reordered.splice(adjustedTo, 0, moved);

      try {
        for (let i = 0; i < reordered.length; i++) {
          const expected = i + 1;
          if (reordered[i].sortOrder !== expected) {
            await updateBookingType({
              variables: { id: reordered[i].id, sortOrder: expected },
            });
          }
        }
        await btQuery.refetch();
        toast.push({
          kind: "success",
          title: "Order updated",
          description: "Service order saved.",
        });
      } catch (e) {
        const msg = e instanceof Error ? e.message : "Unknown error";
        toast.push({
          kind: "error",
          title: "Couldn't reorder",
          description: msg,
        });
      }
    },
    [services, updateBookingType, btQuery, toast],
  );

  const initialLoad =
    (btQuery.loading && !btQuery.data) || (ppQuery.loading && !ppQuery.data);

  return (
    <div className="px-3 py-4 space-y-5">
      <div className="px-2">
        <h2 className="font-display text-[20px] font-medium tracking-tight">
          Catalog
        </h2>
        <p className="text-[11px] text-ink-4 mt-0.5">
          {initialLoad ? (
            <span className="inline-block w-32 h-3 bg-canvas-2 rounded pulse-soft" />
          ) : (
            <>
              {services.length} service{services.length !== 1 ? "s" : ""} ·{" "}
              {plans.length} plan{plans.length !== 1 ? "s" : ""}
            </>
          )}
        </p>
      </div>

      {/* Services */}
      <div>
        <div className="flex items-center justify-between px-2 mb-1.5">
          <span className="text-[10.5px] uppercase tracking-wider text-ink-4 font-semibold">
            Services
          </span>
          <NavLink
            to="services/new"
            className={({ isActive }) =>
              `w-5 h-5 rounded flex items-center justify-center transition ${
                isActive
                  ? "bg-tide text-paper-hi"
                  : "text-ink-4 hover:bg-paper-2 hover:text-ink-2"
              }`
            }
            title="New service"
          >
            <Plus size={12} strokeWidth={2.4} />
          </NavLink>
        </div>
        {initialLoad ? (
          <div className="space-y-1 px-2">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="h-9 bg-canvas-2 rounded pulse-soft"
              />
            ))}
          </div>
        ) : services.length === 0 ? (
          <div className="px-2 text-[11.5px] text-ink-5">
            No services yet.
          </div>
        ) : (
          <div className="space-y-0.5">
            {services.map((svc, idx) => {
              const isDragging = drag?.fromIdx === idx;
              const showAbove =
                drag !== null && drag.toIdx === idx && drag.fromIdx !== idx;
              const showBelow =
                drag !== null &&
                drag.toIdx === services.length &&
                idx === services.length - 1;
              return (
                <ServiceItem
                  key={svc.id}
                  svc={svc}
                  plansById={plansById}
                  dragging={isDragging}
                  dropAbove={showAbove}
                  dropBelow={showBelow}
                  onDragStart={() => setDrag({ fromIdx: idx, toIdx: idx })}
                  onDragOver={(e) => {
                    if (drag === null) return;
                    e.preventDefault();
                    e.dataTransfer.dropEffect = "move";
                    // Use the cursor position relative to the row to decide
                    // whether the drop target is "above this row" or "below
                    // this row" (= above the next row).
                    const rect = (
                      e.currentTarget as HTMLElement
                    ).getBoundingClientRect();
                    const halfway = rect.top + rect.height / 2;
                    const dropAbove = e.clientY < halfway;
                    const newToIdx = dropAbove ? idx : idx + 1;
                    if (drag.toIdx !== newToIdx) {
                      setDrag({ ...drag, toIdx: newToIdx });
                    }
                  }}
                  onDragEnd={() => setDrag(null)}
                  onDrop={(e) => {
                    e.preventDefault();
                    if (drag !== null) {
                      const captured = drag;
                      setDrag(null);
                      handleReorder(captured.fromIdx, captured.toIdx);
                    }
                  }}
                />
              );
            })}
          </div>
        )}
      </div>

      {/* Price plans */}
      <div>
        <div className="flex items-center justify-between px-2 mb-1.5">
          <span className="text-[10.5px] uppercase tracking-wider text-ink-4 font-semibold">
            Price plans
          </span>
          <NavLink
            to="plans/new"
            className={({ isActive }) =>
              `w-5 h-5 rounded flex items-center justify-center transition ${
                isActive
                  ? "bg-tide text-paper-hi"
                  : "text-ink-4 hover:bg-paper-2 hover:text-ink-2"
              }`
            }
            title="New plan"
          >
            <Plus size={12} strokeWidth={2.4} />
          </NavLink>
        </div>
        {initialLoad ? (
          <div className="space-y-1 px-2">
            {[1, 2].map((i) => (
              <div
                key={i}
                className="h-9 bg-canvas-2 rounded pulse-soft"
              />
            ))}
          </div>
        ) : plans.length === 0 ? (
          <div className="px-2 text-[11.5px] text-ink-5">
            No plans yet.
          </div>
        ) : (
          <div className="space-y-0.5">
            {plans.map((plan) => (
              <PlanItem
                key={plan.id}
                plan={plan}
                usageCount={planUsage.get(plan.id) ?? 0}
              />
            ))}
          </div>
        )}
      </div>

      {/* Footer hint */}
      {plans.length > 0 && (
        <div className="px-2 pt-3 hair-t border-t flex items-start gap-2 text-[10.5px] text-ink-4 leading-relaxed">
          <AlertTriangle
            size={11}
            strokeWidth={2}
            className="text-spark mt-0.5 flex-shrink-0"
          />
          <span>
            Edits to a plan affect every service that uses it. Existing bookings
            keep their quoted price.
          </span>
        </div>
      )}
    </div>
  );
}
